import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Input,
  Segment,
  Header,
  Dropdown,
  Grid,
  Button,
  Icon,
  Transition,
} from "semantic-ui-react";
import Corr from "./Corr";

export default function CorrSearch() {
  const { currentUser } = useSelector((state) => state.auth);
  const { currentUserProfile } = useSelector(
    (state) => state.profile
  );

  const { corrs = [] } = useSelector((state) => state.corrs);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);

  const statusOptions = [
    { key: "all", text: "All", value: "all" },
    { key: "open", text: "Open", value: "open" },
    { key: "closed", text: "Closed", value: "closed" },
  ];

  const filteredCorrs = useMemo(() => {
    if (!currentUser?.email) {
      return [];
    }

    const userEmail = currentUser.email.trim().toLowerCase();
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const canViewAll =
      currentUserProfile?.isAdmin === true &&
      currentUserProfile?.viewAll === true;

    let results = [...corrs];

    /*
     * Regular users can only search correspondence where they
     * are either the agent or one of the assigned users.
     *
     * Admins with viewAll enabled can search every record.
     */
    if (!canViewAll) {
      results = results.filter((corr) => {
        const agentEmail =
          typeof corr.agentEmail === "string"
            ? corr.agentEmail.trim().toLowerCase()
            : "";

        const assignedEmails = Array.isArray(corr.assignedEmails)
          ? corr.assignedEmails
          : [];

        const isAgent = agentEmail === userEmail;

        const isAssigned = assignedEmails.some(
          (email) =>
            typeof email === "string" &&
            email.trim().toLowerCase() === userEmail
        );

        return isAgent || isAssigned;
      });
    }

    if (statusFilter !== "all") {
      results = results.filter(
        (corr) =>
          corr.ticketStatus?.trim().toLowerCase() ===
          statusFilter.toLowerCase()
      );
    }

    /*
     * This runs after every keystroke:
     * "a" produces broad results.
     * "ab" immediately narrows those results.
     */
   if (normalizedSearch) {
  results = results.filter((corr) => {
    const assignedEmails = Array.isArray(corr.assignedEmails)
      ? corr.assignedEmails
      : [];

    const corrNumber =
      corr.corrNumber || corr.id?.slice(0, 9) || "";

    const corrNumberMatch = corrNumber
      .toLowerCase()
      .includes(normalizedSearch);

    const assignedEmailMatch = assignedEmails.some(
      (email) =>
        typeof email === "string" &&
        email.toLowerCase().includes(normalizedSearch)
    );

    const agentEmailMatch = corr.agentEmail
      ?.toLowerCase()
      .includes(normalizedSearch);

    const addressedToMatch = corr.addressedTo
      ?.toLowerCase()
      .includes(normalizedSearch);

    const subjectMatch = corr.subject
      ?.toLowerCase()
      .includes(normalizedSearch);

    return (
      corrNumberMatch ||
      assignedEmailMatch ||
      agentEmailMatch ||
      addressedToMatch ||
      subjectMatch
    );
  });
}

    return results;
  }, [
    corrs,
    currentUser,
    currentUserProfile,
    searchTerm,
    statusFilter,
  ]);

  const searchIsActive =
    searchTerm.trim() !== "" || statusFilter !== "all";

  return (
    <>
      <Button
        icon
        basic
        labelPosition="left"
        color="blue"
        onClick={() => setOpen((previousOpen) => !previousOpen)}
        style={{ marginBottom: "1em" }}
      >
        <Icon name="search" />
        {open ? "Hide Search" : "Search"}
      </Button>

      <Transition
        visible={open}
        animation="slide down"
        duration={300}
      >
        <Segment>
          <Header as="h3">Search Correspondence</Header>

          <Grid stackable>
            <Grid.Row columns={2}>
              <Grid.Column width={10}>
                <Input
                  fluid
                  icon="search"
                  placeholder="Search correspondence number, email, subject, or addressed to..."
                  value={searchTerm}
                  onChange={(event) =>
                    setSearchTerm(event.target.value)
                  }
                />
              </Grid.Column>

              <Grid.Column width={6}>
                <Dropdown
                  fluid
                  selection
                  options={statusOptions}
                  value={statusFilter}
                  onChange={(event, data) =>
                    setStatusFilter(data.value)
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>

          <div style={{ marginTop: "2em" }}>
            {!searchIsActive ? (
              <Header as="h4" color="grey">
                Start typing or select a status to search.
              </Header>
            ) : filteredCorrs.length > 0 ? (
              <>
                <Header as="h4">
                  {filteredCorrs.length}{" "}
                  {filteredCorrs.length === 1
                    ? "result"
                    : "results"}
                </Header>

                {filteredCorrs.map((corr) => (
                  <Corr key={corr.id} corr={corr} />
                ))}
              </>
            ) : (
              <Header as="h4" color="grey">
                No matching correspondence found.
              </Header>
            )}
          </div>
        </Segment>
      </Transition>
    </>
  );
}